import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Users, Search, Eye, Settings, Trash2, Star, Target, Plus, X, Check, UserPlus,
  CheckSquare, Square, Archive, RefreshCw, ArrowUpDown, Filter, AlertTriangle, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import ClearanceBadge from '../components/ClearanceBadge';
import { AthleteAvatar } from '../components/AthleteAvatar';

const API = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  'Goalkeeper', 'Right Back', 'Center Back', 'Left Back', 'Defender',
  'Defensive Midfielder', 'Central Midfielder', 'Midfielder',
  'Attacking Midfielder', 'Right Winger', 'Left Winger', 'Forward', 'Striker'
];
const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];
const FEET = ['Right', 'Left', 'Both'];
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

// ─── Quick Assign Popover ─────────────────────────────────────
const QuickAssignPopover = ({ athleteId, assignedStaff, allStaff, onUpdate, token }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = async (staffId) => {
    if (busy) return;
    const current = new Set(assignedStaff);
    current.has(staffId) ? current.delete(staffId) : current.add(staffId);
    const newIds = [...current];
    onUpdate(athleteId, newIds);
    setBusy(true);
    try {
      await axios.put(`${API}/api/athletes/${athleteId}/assignments`, { staff_user_ids: newIds }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { onUpdate(athleteId, assignedStaff); toast.error('Failed to update'); }
    finally { setBusy(false); }
  };

  const filtered = allStaff.filter(s => !search || s.full_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      <Button size="sm" variant="outline" onClick={() => setOpen(!open)}
        className={`h-7 px-1.5 gap-1 text-[10px] font-medium ${open ? 'border-pfap-green text-pfap-green' : ''}`}
        data-testid={`quick-assign-btn-${athleteId}`} title="Assign staff">
        <Users size={12} />
        {assignedStaff.length > 0 && (
          <span className="bg-pfap-green text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]">{assignedStaff.length}</span>
        )}
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30"
          data-testid={`quick-assign-popover-${athleteId}`}>
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full h-7 pl-7 pr-2 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-pfap-green"
                data-testid={`quick-assign-search-${athleteId}`} />
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto py-1">
            {filtered.length === 0 && <p className="text-xs text-gray-400 px-3 py-2">No staff found</p>}
            {filtered.map(s => {
              const isAssigned = assignedStaff.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggle(s.id)} disabled={busy}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${isAssigned ? 'bg-pfap-green/5 dark:bg-pfap-green/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'} ${busy ? 'opacity-50' : ''}`}
                  data-testid={`quick-assign-option-${athleteId}-${s.id}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isAssigned ? 'bg-pfap-green border-pfap-green' : 'border-gray-300 dark:border-gray-600'}`}>
                    {isAssigned && <Check size={10} className="text-white" />}
                  </div>
                  <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{s.full_name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
const AdminAthletes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [cardsData, setCardsData] = useState({});
  const [allStaff, setAllStaff] = useState([]);
  const [flagsData, setFlagsData] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterPosition, setFilterPosition] = useState('All');
  const [filterSquad, setFilterSquad] = useState('All');
  const [filterAvailability, setFilterAvailability] = useState('All');
  const [filterStaff, setFilterStaff] = useState('All');
  const [filterFlagged, setFilterFlagged] = useState('All');
  const [filterTag, setFilterTag] = useState('All');
  const [statusTab, setStatusTab] = useState('active'); // 'active' | 'inactive' | 'all'
  // Phase 82.3 — Season-scope selector. Defaults to 'current' so the
  // operational list reflects the current-season roster only. Admins
  // can switch to All / Previous / Not in Current for historical or
  // re-add workflows.
  const [seasonScope, setSeasonScope] = useState('current');
  const [sortBy, setSortBy] = useState('name');

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkSquad, setBulkSquad] = useState('');
  const [bulkStaff, setBulkStaff] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showBulkStaff, setShowBulkStaff] = useState(false);
  const [smartFilters, setSmartFilters] = useState([]); // ['injured','no-wellness','missed-last','pdp-overdue']
  const [coachRows, setCoachRows] = useState({}); // keyed by athlete_id

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  // Phase 78 — Add-Existing flow + duplicate-warning modal state.
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { matches:[], pendingPayload }
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [existingPickerLoading, setExistingPickerLoading] = useState(false);
  const [existingPicker, setExistingPicker] = useState({ search: '', athletes: [] });
  const [creating, setCreating] = useState(false);
  const [newAthlete, setNewAthlete] = useState({
    full_name: '', date_of_birth: '', nationality: '', position: '',
    dominant_foot: '', squad: '', email: '', phone: '', gender: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'Admin';
  const isStaff = user?.role === 'Staff';
  const canSeeDemo = isAdmin || isStaff;
  // Test Player ID — pinned to top of lists for Admin/Staff as onboarding reference
  const TEST_PLAYER_ID = '0e378572-d49c-4bd2-8a92-6a166f84620f';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [athRes, cardsRes, staffRes, flagsRes, tagsRes, mapRes, coachRes] = await Promise.all([
        axios.get(`${API}/api/athletes?status=${statusTab}&season_scope=${seasonScope}`, { headers }),
        axios.get(`${API}/api/athletes/cards-data?season_scope=${seasonScope}`, { headers }),
        axios.get(`${API}/api/athletes/staff-list`, { headers }),
        axios.get(`${API}/api/flags`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/tags`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/tags/map`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/dashboard/coach-today`, { headers }).catch(() => ({ data: null })),
      ]);
      setAthletes(athRes.data || []);
      setCardsData(cardsRes.data || {});
      setAllStaff(staffRes.data || []);
      setFlagsData(flagsRes.data || []);
      setTags(tagsRes.data || []);
      setTagMap(mapRes.data || {});
      const rows = coachRes.data?.athlete_rows || [];
      const rowMap = {};
      rows.forEach(r => { rowMap[r.id] = r; });
      setCoachRows(rowMap);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, statusTab, seasonScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Flagged athlete IDs
  const flaggedIds = useMemo(() => {
    const ids = new Set();
    flagsData.filter(f => f.status === 'Active').forEach(f => ids.add(f.athlete_id));
    return ids;
  }, [flagsData]);

  // Dynamic filter options
  const positions = useMemo(() => ['All', ...new Set(athletes.map(a => a.position).filter(Boolean))], [athletes]);
  const squads = useMemo(() => ['All', ...new Set(athletes.map(a => a.squad).filter(Boolean))], [athletes]);

  // Filtering + sorting
  const filtered = useMemo(() => {
    let result = athletes.filter(a => {
      if (search && !a.full_name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterPosition !== 'All' && a.position !== filterPosition) return false;
      if (filterSquad !== 'All' && a.squad !== filterSquad) return false;
      const card = cardsData[a.id] || {};
      if (filterAvailability !== 'All' && card.availability !== filterAvailability) return false;
      if (filterStaff !== 'All') {
        if (filterStaff === 'Unassigned' && (a.assigned_staff || []).length > 0) return false;
        if (filterStaff !== 'Unassigned' && !(a.assigned_staff || []).includes(filterStaff)) return false;
      }
      if (filterFlagged === 'Flagged' && !flaggedIds.has(a.id)) return false;
      if (filterFlagged === 'Clean' && flaggedIds.has(a.id)) return false;
      if (filterTag !== 'All' && !(tagMap[a.id] || []).includes(filterTag)) return false;
      // Smart filter chips (uses coach-today signals)
      if (smartFilters.length > 0) {
        const row = coachRows[a.id] || {};
        if (smartFilters.includes('injured') && row.availability !== 'Injured') return false;
        if (smartFilters.includes('no-wellness') && row.wellness_today !== false) return false;
        if (smartFilters.includes('missed-last') && !row.missed_last_session) return false;
        if (smartFilters.includes('pdp-overdue') && !row.pdp_overdue) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      // Pin Test Player to the top for Admin/Staff only — onboarding reference profile
      if (canSeeDemo) {
        if (a.id === TEST_PLAYER_ID && b.id !== TEST_PLAYER_ID) return -1;
        if (b.id === TEST_PLAYER_ID && a.id !== TEST_PLAYER_ID) return 1;
      }
      if (sortBy === 'name') return (a.full_name || '').localeCompare(b.full_name || '');
      if (sortBy === 'rating') return ((cardsData[b.id]?.form_rating || 0) - (cardsData[a.id]?.form_rating || 0));
      if (sortBy === 'squad') return (a.squad || '').localeCompare(b.squad || '');
      return 0;
    });
    return result;
  }, [athletes, search, filterPosition, filterSquad, filterAvailability, filterStaff, filterFlagged, filterTag, sortBy, cardsData, flaggedIds, canSeeDemo, tagMap, smartFilters, coachRows]);

  // Bulk handlers
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectAll = () => setSelected(new Set(filtered.map(a => a.id)));
  const clearSelection = () => setSelected(new Set());
  const isAllSelected = filtered.length > 0 && filtered.every(a => selected.has(a.id));

  const executeBulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (action === 'archive') {
        if (!window.confirm(`Archive ${ids.length} athlete(s)? They will be set to inactive.`)) { setBulkBusy(false); return; }
        await axios.post(`${API}/api/athletes/bulk/status`, { athlete_ids: ids, status: 'inactive' }, { headers });
        toast.success(`${ids.length} athlete(s) archived`);
      } else if (action === 'activate') {
        await axios.post(`${API}/api/athletes/bulk/status`, { athlete_ids: ids, status: 'active' }, { headers });
        toast.success(`${ids.length} athlete(s) activated`);
      } else if (action === 'squad' && bulkSquad) {
        await axios.post(`${API}/api/athletes/bulk/squad`, { athlete_ids: ids, squad: bulkSquad }, { headers });
        toast.success(`${ids.length} athlete(s) moved to ${bulkSquad}`);
      } else if (action === 'assign' && bulkStaff.length > 0) {
        await axios.post(`${API}/api/athletes/bulk/assign-staff`, { athlete_ids: ids, staff_user_ids: bulkStaff }, { headers });
        toast.success(`Staff assigned to ${ids.length} athlete(s)`);
      } else if (action === 'delete') {
        if (!window.confirm(`PERMANENTLY DELETE ${ids.length} athlete(s)? This cannot be undone.`)) { setBulkBusy(false); return; }
        await axios.post(`${API}/api/athletes/bulk/delete`, { athlete_ids: ids }, { headers });
        toast.success(`${ids.length} athlete(s) deleted`);
      }
      clearSelection();
      setBulkSquad('');
      setBulkStaff([]);
      setShowBulkStaff(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk operation failed');
    } finally { setBulkBusy(false); }
  };

  const handleAssignmentUpdate = (athleteId, newStaffIds) => {
    setAthletes(prev => prev.map(a => a.id === athleteId ? { ...a, assigned_staff: newStaffIds } : a));
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await axios.delete(`${API}/api/athletes/${id}`, { headers });
      setAthletes(prev => prev.filter(a => a.id !== id));
    } catch { /* silent */ }
  };

  const handleCreate = async (skipDuplicateCheck = false) => {
    if (!newAthlete.full_name?.trim()) { toast.error('Name is required'); return; }
    if (!newAthlete.date_of_birth) { toast.error('Date of birth is required'); return; }
    if (!newAthlete.nationality?.trim()) { toast.error('Nationality is required'); return; }
    if (!newAthlete.position) { toast.error('Position is required'); return; }
    const payload = { ...newAthlete };
    ['email','phone','dominant_foot','squad','gender'].forEach(k => { if (!payload[k]) delete payload[k]; });

    // Phase 78 — duplicate detection before save (unless user already
    // confirmed via the warning modal).
    if (!skipDuplicateCheck) {
      setDuplicateChecking(true);
      try {
        const q = new URLSearchParams({
          full_name: payload.full_name,
          ...(payload.date_of_birth ? { date_of_birth: payload.date_of_birth } : {}),
          ...(payload.nationality ? { nationality: payload.nationality } : {}),
          ...(payload.email ? { parent_email: payload.email } : {}),
          ...(payload.phone ? { parent_phone: payload.phone } : {}),
        });
        const res = await axios.get(`${API}/api/athletes/duplicate-check?${q.toString()}`, { headers });
        if (res.data?.matches?.length > 0) {
          setDuplicateWarning({ matches: res.data.matches, pendingPayload: payload });
          setDuplicateChecking(false);
          return;
        }
      } catch { /* non-fatal: skip duplicate check on error */ }
      setDuplicateChecking(false);
    }

    setCreating(true);
    try {
      await axios.post(`${API}/api/athletes`, payload, { headers });
      toast.success('Athlete created');
      setShowCreate(false);
      setDuplicateWarning(null);
      setNewAthlete({ full_name: '', date_of_birth: '', nationality: '', position: '', dominant_foot: '', squad: '', email: '', phone: '', gender: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  // Phase 78 — load athletes-not-in-current-season list for the Add Existing picker.
  const loadExistingPicker = useCallback(async (search = '') => {
    setExistingPickerLoading(true);
    try {
      // Find the current season
      const seasons = await axios.get(`${API}/api/seasons/current`, { headers });
      const curId = seasons.data?.id;
      if (!curId) { toast.error('No current season set'); return; }
      const res = await axios.get(
        `${API}/api/athletes/not-in-season/${curId}${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        { headers }
      );
      setExistingPicker({ search, athletes: res.data?.athletes || [] });
    } catch (err) { toast.error('Failed to load existing athletes'); }
    finally { setExistingPickerLoading(false); }
  }, [headers]);

  // Phase 78 — call /add to (re-)link an existing athlete to the current season.
  const handleAddExisting = async (athleteId, athleteName) => {
    try {
      const seasons = await axios.get(`${API}/api/seasons/current`, { headers });
      const curId = seasons.data?.id;
      if (!curId) { toast.error('No current season set'); return; }
      await axios.post(`${API}/api/athletes/${athleteId}/seasons/${curId}/add`, {}, { headers });
      toast.success(`${athleteName} added to current season`);
      setShowAddExisting(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  // Quick filter presets
  const applyPreset = (preset) => {
    setSearch(''); setFilterPosition('All'); setFilterSquad('All'); setFilterAvailability('All'); setFilterStaff('All'); setFilterFlagged('All');
    if (preset === 'unassigned') setFilterStaff('Unassigned');
    if (preset === 'flagged') setFilterFlagged('Flagged');
    if (preset === 'injured') setFilterAvailability('Injured');
  };

  const hasActiveFilters = filterPosition !== 'All' || filterSquad !== 'All' || filterAvailability !== 'All' || filterStaff !== 'All' || filterFlagged !== 'All' || filterTag !== 'All' || search;
  const clearFilters = () => { setSearch(''); setFilterPosition('All'); setFilterSquad('All'); setFilterAvailability('All'); setFilterStaff('All'); setFilterFlagged('All'); setFilterTag('All'); };

  const getAvailColor = (s) => s === 'Injured' ? 'bg-red-50 text-red-600 border-red-200' : s === 'Limited' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200';
  const getFormColor = (r) => !r ? 'text-gray-400 dark:text-gray-500' : r >= 7.5 ? 'text-emerald-600' : r >= 6 ? 'text-amber-600' : 'text-red-600';
  const getPositionColor = (pos) => ({ 'Goalkeeper': 'bg-amber-50 text-amber-700', 'Defender': 'bg-blue-50 text-blue-700', 'Center Back': 'bg-blue-50 text-blue-700', 'Right Back': 'bg-sky-50 text-sky-700', 'Midfielder': 'bg-purple-50 text-purple-700', 'Forward': 'bg-rose-50 text-rose-700' })[pos] || 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300';
  const fitCount = Object.values(cardsData).filter(c => c.availability === 'Fit').length;
  const injuredCount = Object.values(cardsData).filter(c => c.availability === 'Injured').length;

  return (
    <Layout>
    <div className="space-y-4" data-testid="athletes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="page-title">Athletes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {athletes.length} registered &middot; <span className="text-emerald-600">{fitCount} fit</span>
            {injuredCount > 0 && <> &middot; <span className="text-red-600">{injuredCount} injured</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreate(true)} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="create-athlete-btn">
            <Plus size={16} className="mr-1.5" /> Create New
          </Button>
          <Button onClick={() => { setShowAddExisting(true); loadExistingPicker(''); }} variant="outline" data-testid="add-existing-athlete-btn">
            <UserPlus size={16} className="mr-1.5" /> Add Existing
          </Button>
        </div>
      </div>

      {/* Status tabs + Search + Filters */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="athlete-status-tabs">
          {[
            { key: 'active', label: 'Active' },
            { key: 'inactive', label: 'Inactive' },
            { key: 'all', label: 'All' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setStatusTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                statusTab === t.key
                  ? 'bg-pfap-green text-white border-pfap-green shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              data-testid={`status-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
          {/* Phase 82.3 — Season scope selector. Default 'Current Season'
              keeps operational views clean; admins can switch to
              All / Previous / Not in Current for historical lookups
              and 'Add existing athlete' flows. */}
          {isAdmin && (
            <div className="ml-auto flex items-center gap-2" data-testid="season-scope-control">
              <label className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Season</label>
              <select
                value={seasonScope}
                onChange={(e) => setSeasonScope(e.target.value)}
                className="text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-pfap-green"
                data-testid="season-scope-select"
              >
                <option value="current">Current Season</option>
                <option value="all">All Athletes</option>
                <option value="previous">Previous Season</option>
                <option value="not_in_current">Not in Current</option>
              </select>
            </div>
          )}
        </div>

        {/* Smart filter chips — surfaces urgent decisions at a glance */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2 mt-2" data-testid="smart-filter-chips">
          {[
            { key: 'injured', label: 'Injured' },
            { key: 'no-wellness', label: 'No wellness today' },
            { key: 'missed-last', label: 'Missed last session' },
            { key: 'pdp-overdue', label: 'PDP overdue' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setSmartFilters(prev => prev.includes(f.key) ? prev.filter(k => k !== f.key) : [...prev, f.key])}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                smartFilters.includes(f.key)
                  ? 'bg-red-500 text-white border-red-500 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              data-testid={`smart-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="athletes-filters">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search athletes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
          </div>
          <select value={filterPosition} onChange={e => setFilterPosition(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-position">
            {positions.map(p => <option key={p} value={p}>{p === 'All' ? 'All Positions' : p}</option>)}
          </select>
          <select value={filterSquad} onChange={e => setFilterSquad(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-squad">
            {squads.map(s => <option key={s} value={s}>{s === 'All' ? 'All Squads' : s}</option>)}
          </select>
          <select value={filterAvailability} onChange={e => setFilterAvailability(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-availability">
            <option value="All">All Status</option>
            <option value="Fit">Fit</option>
            <option value="Injured">Injured</option>
          </select>
          <select value={filterStaff} onChange={e => setFilterStaff(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-staff">
            <option value="All">All Staff</option>
            <option value="Unassigned">Unassigned</option>
            {allStaff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
          <select value={filterFlagged} onChange={e => setFilterFlagged(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-flagged">
            <option value="All">All Alerts</option>
            <option value="Flagged">Flagged</option>
            <option value="Clean">Clean</option>
          </select>
          <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="filter-tag">
            <option value="All">All Tags</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300" data-testid="sort-by">
            <option value="name">Sort: Name</option>
            <option value="rating">Sort: Rating</option>
            <option value="squad">Sort: Squad</option>
          </select>
        </div>

        {/* Quick Presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400"><Filter size={12} className="inline mr-1" />Quick:</span>
          <button onClick={() => applyPreset('unassigned')} className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-testid="preset-unassigned">Unassigned</button>
          <button onClick={() => applyPreset('flagged')} className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-testid="preset-flagged">Flagged</button>
          <button onClick={() => applyPreset('injured')} className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-testid="preset-injured">Injured</button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 hover:bg-red-100" data-testid="clear-filters">Clear Filters</button>
          )}
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} of {athletes.length}</span>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {isAdmin && selected.size > 0 && (
        <div className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2.5 bg-pfap-green/5 dark:bg-pfap-green/10 border border-pfap-green/20 rounded-lg" data-testid="bulk-action-bar">
          <Badge className="bg-pfap-green text-white text-xs">{selected.size} selected</Badge>
          <Button size="sm" variant="outline" onClick={clearSelection} className="h-7 text-xs" data-testid="bulk-deselect">
            <X size={12} className="mr-1" /> Deselect
          </Button>
          <div className="border-l border-gray-300 dark:border-gray-600 h-5 mx-1" />

          <Button size="sm" variant="outline" onClick={() => executeBulk('archive')} disabled={bulkBusy} className="h-7 text-xs" data-testid="bulk-archive">
            <Archive size={12} className="mr-1" /> Archive
          </Button>
          <Button size="sm" variant="outline" onClick={() => executeBulk('activate')} disabled={bulkBusy} className="h-7 text-xs" data-testid="bulk-activate">
            <RefreshCw size={12} className="mr-1" /> Activate
          </Button>

          <select value={bulkSquad} onChange={e => setBulkSquad(e.target.value)}
            className="h-7 px-2 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-testid="bulk-squad-select">
            <option value="">Move to squad...</option>
            {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {bulkSquad && (
            <Button size="sm" onClick={() => executeBulk('squad')} disabled={bulkBusy} className="h-7 text-xs bg-pfap-green text-white" data-testid="bulk-squad-apply">Apply</Button>
          )}

          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setShowBulkStaff(!showBulkStaff)} className="h-7 text-xs" data-testid="bulk-assign-staff-btn">
              <UserPlus size={12} className="mr-1" /> Assign Staff {bulkStaff.length > 0 && `(${bulkStaff.length})`}
            </Button>
            {showBulkStaff && (
              <div className="absolute left-0 top-full mt-1 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30 max-h-40 overflow-y-auto py-1" data-testid="bulk-staff-dropdown">
                {allStaff.map(s => (
                  <button key={s.id} onClick={() => setBulkStaff(prev => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${bulkStaff.includes(s.id) ? 'bg-pfap-green border-pfap-green' : 'border-gray-300 dark:border-gray-600'}`}>
                      {bulkStaff.includes(s.id) && <Check size={9} className="text-white" />}
                    </div>
                    <span className="text-xs text-gray-800 dark:text-gray-200">{s.full_name}</span>
                  </button>
                ))}
                {bulkStaff.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                    <Button size="sm" onClick={() => { executeBulk('assign'); setShowBulkStaff(false); }} disabled={bulkBusy} className="w-full h-7 text-xs bg-pfap-green text-white" data-testid="bulk-assign-apply">
                      Assign to {selected.size} athlete(s)
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <Button size="sm" variant="outline" onClick={() => executeBulk('delete')} disabled={bulkBusy} className="h-7 text-xs text-red-600 hover:bg-red-50 ml-auto" data-testid="bulk-delete">
            <Trash2 size={12} className="mr-1" /> Delete
          </Button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-[180px] rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Select All */}
          {isAdmin && filtered.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={isAllSelected ? clearSelection : selectAll} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400" data-testid="select-all-btn">
                {isAllSelected ? <CheckSquare size={14} className="text-pfap-green" /> : <Square size={14} />}
                {isAllSelected ? 'Deselect All' : `Select All (${filtered.length})`}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="athletes-grid">
            {filtered.map(athlete => {
              const card = cardsData[athlete.id] || {};
              const isSelected = selected.has(athlete.id);
              const isFlagged = flaggedIds.has(athlete.id);
              return (
                <Card key={athlete.id}
                  className={`bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-150 group ${isSelected ? 'ring-2 ring-pfap-green border-pfap-green' : ''}`}
                  data-testid={`athlete-card-${athlete.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {isAdmin && (
                          <button onClick={() => toggleSelect(athlete.id)} className="mt-0.5 flex-shrink-0" data-testid={`select-${athlete.id}`}>
                            {isSelected ? <CheckSquare size={16} className="text-pfap-green" /> : <Square size={16} className="text-gray-300 dark:text-gray-600 hover:text-gray-500" />}
                          </button>
                        )}
                        <AthleteAvatar
                          photoUrl={athlete.profile_photo}
                          thumbUrl={athlete.profile_photo_thumbnail}
                          name={athlete.full_name}
                          size="sm"
                          className="flex-shrink-0"
                          testId={`athlete-avatar-${athlete.id}`}
                        />
                        <div className="min-w-0">
                          <button onClick={() => navigate(`/athlete/${athlete.id}/360`)}
                            className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-emerald-600 transition-colors text-left truncate block w-full"
                            data-testid={`athlete-name-${athlete.id}`}>
                            {athlete.full_name}
                          </button>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <Badge className={`text-[10px] px-1.5 py-0 ${getPositionColor(athlete.position)}`}>{athlete.position || 'N/A'}</Badge>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">{athlete.squad}</span>
                            {athlete.status === 'inactive' && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-gray-100 text-gray-700 border border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600" data-testid={`inactive-badge-${athlete.id}`}>
                                Inactive
                              </Badge>
                            )}
                            {canSeeDemo && athlete.id === TEST_PLAYER_ID && (
                              <Badge
                                className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
                                data-testid="demo-badge"
                                title="Onboarding reference profile — visible only to Admin/Staff"
                              >
                                DEMO
                              </Badge>
                            )}
                          </div>
                          {/* Tag chips */}
                          {(tagMap[athlete.id] || []).length > 0 && (
                            <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`athlete-tags-${athlete.id}`}>
                              {(tagMap[athlete.id] || []).slice(0, 4).map(tid => {
                                const t = tags.find(tg => tg.id === tid);
                                if (!t) return null;
                                return (
                                  <span
                                    key={tid}
                                    className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0 rounded-full border"
                                    style={{ borderColor: (t.color || '#64748b') + '66', color: t.color || '#475569', background: (t.color || '#64748b') + '14' }}
                                    title={t.description || t.name}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color || '#64748b' }} />
                                    {t.name}
                                  </span>
                                );
                              })}
                              {(tagMap[athlete.id] || []).length > 4 && (
                                <span className="text-[9px] text-gray-400">+{(tagMap[athlete.id] || []).length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {isFlagged && <AlertTriangle size={13} className="text-amber-500" title="Has active flag" />}
                        <ClearanceBadge value={card.availability || 'Fit'} testId={`athlete-clearance-${athlete.id}`} />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-3 py-2 px-2 rounded-lg bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center gap-1.5 flex-1" title="Form rating">
                        <Star size={13} className={getFormColor(card.form_rating)} />
                        <span className={`text-xs font-medium ${getFormColor(card.form_rating)}`}>{card.form_rating || '--'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-1" title="PDP Progress">
                        <Target size={13} className="text-blue-500" />
                        {card.pdp_progress != null ? (
                          <div className="flex items-center gap-1.5 flex-1">
                            <Progress value={card.pdp_progress} className="h-1.5 flex-1" />
                            <span className="text-[10px] text-gray-500 w-7 text-right">{card.pdp_progress}%</span>
                          </div>
                        ) : <span className="text-[10px] text-gray-400">--</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button size="sm" onClick={() => navigate(`/athlete/${athlete.id}/360`)} className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`view-360-${athlete.id}`}>
                        <Eye size={13} className="mr-1" /> View 360
                      </Button>
                      {isAdmin && (
                        <>
                          <QuickAssignPopover athleteId={athlete.id} assignedStaff={athlete.assigned_staff || []} allStaff={allStaff} onUpdate={handleAssignmentUpdate} token={token} />
                          <Button size="sm" variant="outline" onClick={() => navigate(`/athletes/${athlete.id}/manage`)} className="h-7 w-7 p-0" data-testid={`manage-${athlete.id}`}>
                            <Settings size={13} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(athlete.id, athlete.full_name)} className="h-7 w-7 p-0 hover:text-red-600" data-testid={`delete-${athlete.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <Users size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No athletes match your filters</p>
          {hasActiveFilters && <Button variant="link" onClick={clearFilters} className="text-xs mt-2">Clear filters</Button>}
        </div>
      )}

      {/* Create Athlete Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="create-athlete-modal">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Athlete</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-gray-700 dark:text-gray-300">Full Name *</Label>
                  <Input value={newAthlete.full_name} onChange={e => setNewAthlete(p => ({...p, full_name: e.target.value}))} placeholder="Full Name" data-testid="create-input-name" /></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Date of Birth *</Label>
                  <Input type="date" value={newAthlete.date_of_birth} onChange={e => setNewAthlete(p => ({...p, date_of_birth: e.target.value}))} data-testid="create-input-dob" /></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Nationality *</Label>
                  <Input value={newAthlete.nationality} onChange={e => setNewAthlete(p => ({...p, nationality: e.target.value}))} placeholder="Nationality" data-testid="create-input-nationality" /></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Position *</Label>
                  <select value={newAthlete.position} onChange={e => setNewAthlete(p => ({...p, position: e.target.value}))}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" data-testid="create-select-position">
                    <option value="">Select Position</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Dominant Foot</Label>
                  <select value={newAthlete.dominant_foot} onChange={e => setNewAthlete(p => ({...p, dominant_foot: e.target.value}))}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" data-testid="create-select-foot">
                    <option value="">Select Foot</option>
                    {FEET.map(f => <option key={f} value={f}>{f}</option>)}
                  </select></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Gender</Label>
                  <select value={newAthlete.gender} onChange={e => setNewAthlete(p => ({...p, gender: e.target.value}))}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" data-testid="create-select-gender">
                    <option value="">— Not set —</option>
                    {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Squad / Team</Label>
                  <select value={newAthlete.squad} onChange={e => setNewAthlete(p => ({...p, squad: e.target.value}))}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" data-testid="create-select-squad">
                    <option value="">Select Squad</option>
                    {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Email</Label>
                  <Input type="email" value={newAthlete.email} onChange={e => setNewAthlete(p => ({...p, email: e.target.value}))} placeholder="athlete@email.com" data-testid="create-input-email" /></div>
                <div><Label className="text-gray-700 dark:text-gray-300">Phone</Label>
                  <Input value={newAthlete.phone} onChange={e => setNewAthlete(p => ({...p, phone: e.target.value}))} placeholder="+351 000 000 000" data-testid="create-input-phone" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowCreate(false)} data-testid="create-cancel-btn">Cancel</Button>
              <Button onClick={() => handleCreate(false)} disabled={creating || duplicateChecking} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="create-submit-btn">
                {duplicateChecking ? 'Checking duplicates…' : creating ? 'Creating...' : 'Create Athlete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 78 — Duplicate Warning Modal. Non-blocking — staff can
          either pick "Add Existing" or click "Create Anyway". */}
      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="duplicate-warning-modal">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
              <AlertTriangle size={22} className="text-amber-600" />
              <div className="flex-1">
                <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">Possible existing athlete found</h2>
                <p className="text-xs text-amber-800 dark:text-amber-300">This athlete may already exist in the system. Review before creating a new identity.</p>
              </div>
              <button onClick={() => setDuplicateWarning(null)} className="text-gray-400 hover:text-gray-600" data-testid="duplicate-warning-close">
                <X size={20} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 max-h-96 overflow-y-auto">
              {duplicateWarning.matches.map((m) => (
                <div key={m.athlete_id}
                     className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                     data-testid={`duplicate-match-${m.athlete_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{m.full_name}</span>
                        <Badge className={
                          m.confidence === 'exact' ? 'bg-red-100 text-red-700' :
                          m.confidence === 'high' ? 'bg-orange-100 text-orange-700' :
                          m.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }>{m.confidence}</Badge>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        DOB {m.date_of_birth || '—'} · {m.nationality || '—'} · matched on {m.reasons?.join(', ')}
                      </p>
                      {m.previous_seasons?.length > 0 && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          Seasons: {m.previous_seasons.slice(0, 3).map(s => `${s.season_name}${s.squad ? ' / ' + s.squad : ''}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm" variant="outline" className="shrink-0 text-[11px] h-7"
                      onClick={() => handleAddExisting(m.athlete_id, m.full_name)}
                      data-testid={`duplicate-add-existing-${m.athlete_id}`}
                    >Use Existing</Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setDuplicateWarning(null)} data-testid="duplicate-warning-cancel">
                Back to form
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => { setDuplicateWarning(null); handleCreate(true); }}
                data-testid="duplicate-warning-create-anyway"
              >Continue Creating New</Button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 78 — Add Existing Athlete picker. */}
      {showAddExisting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="add-existing-modal">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Existing Athlete to Current Season</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Pick an athlete from a previous season who isn&apos;t yet active this season.
                </p>
              </div>
              <button onClick={() => setShowAddExisting(false)} className="text-gray-400 hover:text-gray-600" data-testid="add-existing-close">
                <X size={20} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <Input
                placeholder="Search by name, DOB, nationality, season or squad…"
                value={existingPicker.search}
                onChange={(e) => loadExistingPicker(e.target.value)}
                data-testid="add-existing-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {existingPickerLoading ? (
                <p className="text-sm text-gray-500" data-testid="add-existing-loading">Loading…</p>
              ) : existingPicker.athletes.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="add-existing-empty">
                  No previous-season athletes available to add. Use <b>Create New</b> instead.
                </p>
              ) : (
                existingPicker.athletes.map((a) => (
                  <div key={a.id}
                       className="flex items-center justify-between gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
                       data-testid={`add-existing-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{a.full_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        DOB {a.date_of_birth || '—'} · {a.nationality || '—'}
                        {a.last_squad && <> · last squad {a.last_squad}</>}
                      </p>
                      {a.previous_seasons?.length > 0 && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {a.previous_seasons.slice(0, 3).map(s => s.season_name).join(' · ')}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="bg-pfap-green hover:bg-pfap-green/90 text-white text-[11px] h-7 px-3 shrink-0"
                      onClick={() => handleAddExisting(a.id, a.full_name)}
                      data-testid={`add-existing-add-${a.id}`}
                    >Add</Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default AdminAthletes;
